import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  canReactivateYowanSeminarRecord,
  fetchYowanAdminSeminarDetail,
  getYowanAdminPublicVisibilityStatusUi,
  getYowanPublicVisibilityDiagnostics,
  getYowanPublicVisibilityMessages,
  getYowanSeminarReactivateIssues,
  getYowanSeminarSlotWarningIssues,
} from '../app/yowanContentClient';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const ReadOnlyField = ({ label, value, multiline = false, rows = 3 }) => {
  const safeValue = value || '-';

  return (
    <label className={`c-form__field yowan-admin-readonly-field${multiline ? ' yowan-admin-readonly-field--wide' : ''}`}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea className="c-form__control c-form__control--textarea" readOnly rows={rows} value={safeValue} />
      ) : (
        <input className="c-form__control" readOnly type="text" value={safeValue} />
      )}
    </label>
  );
};

const DetailSection = ({ title, lead, children }) => (
  <section className="yowan-admin-detail-panel yowan-admin-event-detail__section">
    <header className="yowan-admin-event-detail__section-header">
      <h2 className="yowan-admin-detail-panel__title">{title}</h2>
      {lead ? <p className="yowan-admin-detail-panel__lead">{lead}</p> : null}
    </header>
    {children}
  </section>
);

const StatusBadge = ({ label, tone = 'draft' }) => (
  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${tone}`}>
    {label}
  </span>
);

const PublicVisibilityPanel = ({ visibility }) => {
  const statusUi = getYowanAdminPublicVisibilityStatusUi(visibility);
  const messages = getYowanPublicVisibilityMessages(visibility);
  const diagnostics = getYowanPublicVisibilityDiagnostics(visibility);

  return (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">公開面表示判定</p>
        <StatusBadge label={statusUi.label} tone={statusUi.tone} />
      </div>
      {diagnostics.length ? (
        <dl className="yowan-admin-event-detail__definition">
          {diagnostics.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {messages.length ? (
        <ul className="yowan-admin-public-visibility__list">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : (
        <p className="yowan-admin-public-visibility__message">
          公開面に表示できる状態です。申込受付の可否は「申込受付中」と開催コマごとの受付状態で別に制御します。
        </p>
      )}
    </section>
  );
};

const AdminStateNoticePanel = ({ title, issues = [] }) => (
  issues.length ? (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">{title}</p>
      </div>
      <ul className="yowan-admin-public-visibility__list">
        {issues.map((issue) => (
          <li key={issue.code || issue.label}>{issue.message}</li>
        ))}
      </ul>
    </section>
  ) : null
);

const normalizeSeriesKindLabel = (label = '') => (
  label === '一連の講習会' ? 'シリーズ' : label
);

const AdminEventDetailPage = () => {
  const content = yowanPageContent['/admin/events/:eventId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { eventId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminSeminarDetail(eventId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError('セミナー詳細を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [eventId]);

  if (!content) {
    return null;
  }

  const series = record?.series || null;
  const isGrouped = record?.seriesKind === 'grouped';
  const sameSeriesEvents = (record?.sameSeriesEvents || []).filter((item) => item.id && item.id !== record.id);
  const slots = record?.slots || [];
  const headerTitle = record ? `${record.title} 詳細` : content.header.title;
  const notificationHref = appendYowanPreviewQueryIfNeeded('/admin/notifications', preservedDomainSearch);
  const cancelHref = record ? appendYowanPreviewQueryIfNeeded(record.cancelHref || `/admin/events/${record.id}/cancel`, preservedDomainSearch) : '';
  const reactivateHref = record ? appendYowanPreviewQueryIfNeeded(record.reactivateHref || `/admin/events/${record.id}/reactivate`, preservedDomainSearch) : '';
  const cancelLeadText = content.cancelAction?.detailLeadText ||
    '中止は通常の編集保存とは別の専用操作です。公開停止・無効化・開催コマのクローズ・通知ルールに基づく中止通知を、専用ページで確認してから実行します。';
  const reactivateLeadText = content.reactivateAction?.detailLeadText ||
    '運用再開は通常の編集保存とは別の専用操作です。セミナー本体の運用停止状態、または単独セミナーの内部親シリーズ停止状態だけを戻し、公開・申込受付・開催コマの現在値は維持します。';
  const publicVisibilityUi = getYowanAdminPublicVisibilityStatusUi(record?.publicVisibility);
  const canReactivateRecord = canReactivateYowanSeminarRecord(record);
  const slotWarningIssues = getYowanSeminarSlotWarningIssues(record || {});
  const reactivateIssues = getYowanSeminarReactivateIssues(record || {});

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={headerTitle}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch)}>
            セミナー一覧
          </Link>,
          record ? (
            <Link key="edit" to={appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}/edit?kind=seminar`, preservedDomainSearch)}>
              編集
            </Link>
          ) : null,
          record ? (
            <Link key="applications" to={appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}/applications`, preservedDomainSearch)}>
              参加者
            </Link>
          ) : null,
          record ? (
            <Link key="messages" to={appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}/application-messages`, preservedDomainSearch)}>
              申込メッセージ
            </Link>
          ) : null,
          record ? (
            <Link key="mail" to={appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}/applications/mail`, preservedDomainSearch)}>
              参加者メール
            </Link>
          ) : null,
          record && record.isActive !== false ? (
            <Link key="cancel" to={cancelHref}>
              中止
            </Link>
          ) : null,
          record && canReactivateRecord ? (
            <Link key="reactivate" to={reactivateHref}>
              運用再開
            </Link>
          ) : null,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="セミナー詳細を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}

        {!isLoading && !loadError && record ? (
          <div className="yowan-admin-event-detail">
            <DetailSection title="基本情報">
              <div className="yowan-admin-event-detail__status-row">
                <StatusBadge label={normalizeSeriesKindLabel(record.seriesKindLabel) || '-'} tone={isGrouped ? 'review' : 'draft'} />
                <StatusBadge label={record.isPublished ? '公開中' : '非公開'} tone={record.isPublished ? 'published' : 'draft'} />
                <StatusBadge label={record.isActive ? '有効' : '停止中'} tone={record.isActive ? 'published' : 'draft'} />
                <StatusBadge label={publicVisibilityUi.label} tone={publicVisibilityUi.tone} />
              </div>
              <PublicVisibilityPanel visibility={record.publicVisibility} />
              <AdminStateNoticePanel title="開催コマの注意" issues={slotWarningIssues} />
              <div className="yowan-admin-event-detail__grid">
                <ReadOnlyField label="セミナー名" value={record.title} />
                <ReadOnlyField label="サブタイトル" value={record.subtitle} />
                <ReadOnlyField label="主催者" value={record.organizerName} />
                <ReadOnlyField label="セミナー通し参加料金" value={record.fullParticipationFeeLabel} />
                <ReadOnlyField label="参加者" value={`${record.applicationsCount || 0}名`} />
                <ReadOnlyField label="更新日" value={record.updatedAt} />
                <ReadOnlyField label="概要" value={record.summary} multiline rows={3} />
                <ReadOnlyField label="案内文" value={record.guidance} multiline rows={5} />
              </div>
            </DetailSection>

            {isGrouped ? (
              <DetailSection
                title="シリーズ"
                lead="公開面でまとめて表示されるシリーズ情報です。セミナー内の通し参加料金とは別に、シリーズ全体の通し参加料金を持ちます。"
              >
                <div className="yowan-admin-event-detail__grid">
                  <ReadOnlyField label="シリーズ名" value={series?.title} />
                  <ReadOnlyField label="シリーズ補足" value={series?.subtitle} />
                  <ReadOnlyField label="公開URL slug" value={series?.publicSlug} />
                  <ReadOnlyField label="公開パス" value={series?.publicPath || series?.publicHref} />
                  <ReadOnlyField label="正規URL" value={series?.canonicalUrl} />
                  <ReadOnlyField label="シリーズ主催者" value={series?.organizerName} />
                  <ReadOnlyField label="シリーズ通し参加料金" value={series?.fullParticipationFeeLabel} />
                </div>
                {series?.id ? (
                  <div className="yowan-admin-event-detail__related">
                    <p className="yowan-admin-detail-panel__meta">シリーズ管理</p>
                    <div className="yowan-admin-event-detail__related-links">
                      <Link
                        className="yowan-admin-event-detail__related-link"
                        to={appendYowanPreviewQueryIfNeeded(`/admin/events/series/${series.id}`, preservedDomainSearch)}
                      >
                        シリーズ詳細
                      </Link>
                      <Link
                        className="yowan-admin-event-detail__related-link"
                        to={appendYowanPreviewQueryIfNeeded(`/admin/events/series/${series.id}/application-messages`, preservedDomainSearch)}
                      >
                        シリーズ申込メッセージ
                      </Link>
                      {series.publicHref ? (
                        <Link
                          className="yowan-admin-event-detail__related-link"
                          to={appendYowanPreviewQueryIfNeeded(series.publicHref, preservedDomainSearch)}
                        >
                          公開ページ
                        </Link>
                      ) : null}
                      <Link
                        className="yowan-admin-event-detail__related-link"
                        to={appendYowanPreviewQueryIfNeeded(`/admin/events/new?seriesId=${series.id}`, preservedDomainSearch)}
                      >
                        このシリーズにセミナーを追加
                      </Link>
                    </div>
                  </div>
                ) : null}
                <div className="yowan-admin-event-detail__related">
                  <p className="yowan-admin-detail-panel__meta">同じシリーズのセミナー</p>
                  <div className="yowan-admin-event-detail__related-links">
                    {sameSeriesEvents.length ? sameSeriesEvents.map((item) => (
                      <Link
                        key={item.id}
                        className="yowan-admin-event-detail__related-link"
                        to={appendYowanPreviewQueryIfNeeded(`/admin/events/${item.id}`, preservedDomainSearch)}
                      >
                        {item.title || item.id}
                      </Link>
                    )) : (
                      <span className="yowan-admin-event-detail__related-empty">他のセミナーはまだありません</span>
                    )}
                  </div>
                </div>
              </DetailSection>
            ) : null}

            <DetailSection title="開催コマ">
              <div className="yowan-admin-event-detail__slots">
                {slots.length ? slots.map((slot, index) => (
                  <article className="yowan-admin-event-detail__slot" key={slot.id || index}>
                    <header>
                      <p className="yowan-admin-event-detail__slot-eyebrow">コマ {index + 1}</p>
                      <h3 className="yowan-admin-event-detail__slot-title">{slot.title || slot.datetimeLabel || `開催コマ ${index + 1}`}</h3>
                    </header>
                    <dl className="yowan-admin-event-detail__definition">
                      <div>
                        <dt>日時</dt>
                        <dd>{slot.datetimeLabel || '-'}</dd>
                      </div>
                      <div>
                        <dt>場所</dt>
                        <dd>{slot.locationLabel || '-'}</dd>
                      </div>
                      <div>
                        <dt>講師</dt>
                        <dd>{slot.instructorName || '-'}</dd>
                      </div>
                      <div>
                        <dt>定員</dt>
                        <dd>{slot.capacity ? `${slot.capacity}名` : '-'}</dd>
                      </div>
                      <div>
                        <dt>参加費</dt>
                        <dd>{slot.slotFeeLabel || '-'}</dd>
                      </div>
                      {slot.thingsToBring ? (
                        <div>
                          <dt>持ち物</dt>
                          <dd>{slot.thingsToBring}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>受付</dt>
                        <dd>{slot.isOpen ? '受付中' : '停止中'}</dd>
                      </div>
                    </dl>
                  </article>
                )) : (
                  <p className="yowan-admin-form__message">開催コマはまだ登録されていません。</p>
                )}
              </div>
            </DetailSection>

            <DetailSection
              title="セミナー中止"
              lead={cancelLeadText}
            >
              <div className="yowan-admin-form__actions">
                {record.isActive !== false ? (
                  <Link className="c-form__submit" to={cancelHref}>
                    中止専用ページで確認する
                  </Link>
                ) : (
                  <span className="yowan-admin-resource-table__status yowan-admin-resource-table__status--draft">
                    中止済みまたは無効
                  </span>
                )}
                <Link to={notificationHref}>通知履歴へ</Link>
              </div>
            </DetailSection>

            {canReactivateRecord || reactivateIssues.length ? (
              <DetailSection
                title="セミナー運用再開"
                lead={reactivateLeadText}
              >
                <AdminStateNoticePanel title="運用再開が必要な理由" issues={reactivateIssues} />
                <div className="yowan-admin-form__actions">
                  {canReactivateRecord ? (
                    <Link className="c-form__submit" to={reactivateHref}>
                      運用再開専用ページで確認する
                    </Link>
                  ) : (
                    <span className="yowan-admin-resource-table__status yowan-admin-resource-table__status--draft">
                      運用再開対象外
                    </span>
                  )}
                  <Link to={notificationHref}>通知履歴へ</Link>
                </div>
              </DetailSection>
            ) : null}
          </div>
        ) : null}
      </div>
    </AdminBlogShell>
  );
};

export default AdminEventDetailPage;
